import * as React from 'react';

import { Link } from '@natu/next-link';
import { cn } from '@natu/utils/cn';

import { DynamicIcon } from '../DynamicIcon/DynamicIcon';

const PaginationWrapper = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn('mx-auto w-full md:flex md:justify-center', className)}
    {...props}
  />
);
PaginationWrapper.displayName = 'PaginationWrapper';

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn('flex flex-row items-center', className)} {...props} />
  ),
);
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(
  ({ className, ...props }, ref) => <li ref={ref} className={className} {...props} />,
);
PaginationItem.displayName = 'PaginationItem';

const PaginationLink = ({
  className,
  isActive,
  ...props
}: { isActive?: boolean } & React.ComponentProps<'a'>) => {
  if (!props.href) return null;

  return (
    <Link
      aria-current={isActive ? 'page' : undefined}
      href={props.href}
      className={cn(
        'flex h-10 w-10 items-center justify-center rounded-full',
        isActive && 'bg-primary-600 pointer-events-none text-white',
        className,
      )}
      {...props}
    />
  );
};
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({
  className,
  isActive,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to previous page"
    className={cn(
      'flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 md:mr-10',
      !isActive && 'pointer-events-none opacity-50',
      className,
    )}
    {...props}
  >
    <DynamicIcon icon="arrowRight" className="rotate-180 text-gray-800" />
  </PaginationLink>
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({
  className,
  isActive,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to next page"
    className={cn(
      'flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 md:ml-10',
      !isActive && 'pointer-events-none opacity-50',
      className,
    )}
    {...props}
  >
    <DynamicIcon icon="arrowRight" className="text-gray-800" />
  </PaginationLink>
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span
    aria-hidden
    className={cn('flex h-9 w-9 items-center justify-center', className)}
    {...props}
  >
    ...
    <span className="sr-only">More pages</span>
  </span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
  PaginationWrapper,
  PaginationContent,
  PaginationLink,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};
